function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _classPrivateFieldInitSpec(e, t, a) { _checkPrivateRedeclaration(e, t), t.set(e, a); }
function _checkPrivateRedeclaration(e, t) { if (t.has(e)) throw new TypeError("Cannot initialize the same private elements twice on an object"); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _classPrivateFieldGet(e, t) { var r = _classPrivateFieldGet2(t, e); return _classApplyDescriptorGet(e, r); }
function _classPrivateFieldGet2(s, a) { return s.get(_assertClassBrand(s, a)); }
function _assertClassBrand(e, t, n) { if ("function" == typeof e ? e === t : e.has(t)) return arguments.length < 3 ? t : n; throw new TypeError("Private element is not present on this object"); }
function _classApplyDescriptorGet(e, t) { return t.get ? t.get.call(e) : t.value; }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { dismissModal, openModal } from '@market/ember';
import { guidFor } from '@ember/object/internals';
import { hbs } from 'ember-cli-htmlbars';
import { setComponentTemplate } from '@ember/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import assert, { assertBoolean } from '@banking/ui/utils/assert';

//
// NOTE(steckel): The {{#if}} block here is not actually responsible for
// conditionally displaying the modal.
//
// Many implementations of <market-modal-partial> (and <market-modal-full>)
// look like this:
//
// ```
// {{#if showModal}}
//   <template {{open-modal}}>
//     <market-modal-partial></market-modal-partial>
//   </template>
// {{/if}}
// ```
//
// This can be deceiving, because it can appear that the if block is
// responsible for showing and hiding the modal, where that is not actually the
// case.
//
// The {{open-modal}} calls @market/ember's openModal function on the
// <template> tag which moves the template element's child <market-modal-partial>
// element into another position on the DOM under market's modal stack
// manager's control.
//
// To dismiss the modal, implementors must pass the copied <market-modal-partial>
// element to @market/ember's dismissModal function.
//
// Dismissing the modal deletes it from the DOM, resulting in an empty
// <template> element and preventing the modal from being displayed again.
//
// Where the example implementation solves for this (either on purpose or by
// accident) is by wrapping the markup in an if block and calling
// {{open-modal}} unconditionally, so that the condition's truthy evaluation
// coincides with its display **and gets re-rendered once the truthy value
// switches back between falsey and truthy.**
//
// The implementation below seeks to hide these implementation details and
// solve for the asymmetric API where displaying a modal is done in markup and
// dismissal is done in JavaScript. The `active` argument manages the display
// and dismissal without to better follow the "actions up, data down" pattern.
//

// Fixed setComponentTemplate that should already be exposed in TypeScript

export let TagName = /*#__PURE__*/function (TagName) {
  TagName["MarketModalFull"] = "market-modal-full";
  TagName["MarketModalPartial"] = "market-modal-partial";
  return TagName;
}({});
const template = hbs`
<template
    {{did-insert this.didInsert}}
    {{did-update this.onActiveDidUpdate this.active}}
    {{did-update this.onActiveArgDidUpdate @active}}>
  {{#if this.active}}
    {{#let (element this.tagName) as |Tag|}}
      <Tag
          {{on "marketDialogDismissed" this.marketDialogDismissed}}
          {{on "marketDialogLoaded" (or @onLoad (noop))}}
          class={{this.modalClassName}}
          data-market-features="typography"
          ...attributes>
        {{yield}}
      </Tag>
    {{/let}}
  {{/if}}
</template>
`;
export default tagName => {
  var _class, _descriptor, _descriptor2, _guid;
  let Modal = (_class = (_guid = /*#__PURE__*/new WeakMap(), class Modal extends Component {
    constructor(owner, args) {
      super(owner, args);
      _initializerDefineProperty(this, "sentry", _descriptor, this);
      _defineProperty(this, "tagName", tagName);
      _classPrivateFieldInitSpec(this, _guid, {
        writable: true,
        value: guidFor(this)
      });
      _initializerDefineProperty(this, "_active", _descriptor2, this);
      this._active = args.active ?? false;
    }
    _getModalElement() {
      return document.querySelector(`${this.tagName}.${this.modalClassName}`);
    }
    get active() {
      return this._active;
    }
    set active(flag) {
      if (flag === this._active) {
        return;
      }
      this._active = flag;
      this.args.onActiveChange?.(this._active);
    }
    get modalClassName() {
      return `${_classPrivateFieldGet(this, _guid)}-modal`;
    }
    didInsert(element) {
      if (this.active) {
        openModal(element);
      }
    }
    async onActiveDidUpdate(element) {
      if (this.active) {
        openModal(element);
      } else {
        const modelElement = this._getModalElement();
        // TODO(steckel): figure out how you'd recover from this.
        try {
          if (modelElement) {
            // TODO(steckel): find a way to assert HTMLMarketModalPartialElement
            await dismissModal(modelElement);
          }
        } finally {
          this.args.onDismiss?.();
        }
      }
    }
    marketDialogDismissed() {
      this.active = false;
    }
    onActiveArgDidUpdate(_, args) {
      assert(args.length > 0);
      this.active = assertBoolean(args[0]);
    }

    /**
     * This cleans up the modal if it is still opened when the component destroys itself.
     * This is needed because the modal element is added into the dom via the market-context-manager.
     * If the component is destroyed but the modal is opened, modal element itself still lives in the
     * dom even though the Ember component has unmounted.
     */
    willDestroy() {
      super.willDestroy();
      const modelElement = this._getModalElement();
      if (modelElement) {
        try {
          // TODO(steckel): find a way to assert HTMLMarketModalPartialElement
          dismissModal(modelElement);
        } catch (e) {
          this.sentry.captureException(e);
        }
      }
    }
  }), _descriptor = _applyDecoratedDescriptor(_class.prototype, "sentry", [service], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: null
  }), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_active", [tracked], {
    configurable: true,
    enumerable: true,
    writable: true,
    initializer: null
  }), _applyDecoratedDescriptor(_class.prototype, "didInsert", [action], Object.getOwnPropertyDescriptor(_class.prototype, "didInsert"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onActiveDidUpdate", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onActiveDidUpdate"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "marketDialogDismissed", [action], Object.getOwnPropertyDescriptor(_class.prototype, "marketDialogDismissed"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onActiveArgDidUpdate", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onActiveArgDidUpdate"), _class.prototype), _class);
  setComponentTemplate(template, Modal);
  return Modal;
};